import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SearchHitCarousel } from '#/components/SearchHitCarousel/SearchHitCarousel';
import { UnpublishedListing } from '#/components/UnpublishedListing/UnpublishedListing';
import { useAppConfig } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import type { ApiUnpublishedInternship } from '#/modules/listing/internships/api/types/ApiUnpublishedInternship';
import { searchAlgoliaFetchRecommendedListings } from '#/modules/search/algolia/fetch/searchAlgoliaFetchRecommendedListings';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import { searchGetInternshipsRoutePath } from '#/modules/search/internships/routing/searchGetInternshipsRoutePath';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';

import { getUnpublishedInternshipPageSeeMoreText } from './getUnpublishedInternshipPageSeeMoreText';

type Props = {
  unpublishedInternship: ApiUnpublishedInternship;
};

export function UnpublishedInternshipPage({ unpublishedInternship }: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedInternship.id,
  });
  const { hasAts, listingName, id, org } = unpublishedInternship;

  useEffect(() => {
    trackViewListing({
      status: 'unpublished',
      hasAts,
      listingName,
      listingId: id,
      listingType: 'INTERNSHIP',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [hasAts, id, listingName, org]);

  const [recommendedListingsResult] = useFetchFn({
    fn: searchAlgoliaFetchRecommendedListings,
    args: [unpublishedInternship, appConfig],
  });

  const recommendedListings = recommendedListingsResult?.hits ?? [];

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query, locale });

      void navigate(
        searchGetInternshipsRoutePath({
          locale,
          q: parsedQuery,
          filtersByName: { locationType },
        }),
        {
          state: {
            searchFrom: 'Unpublished Internship Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [locale, navigate],
  );

  const seeMoreText = getUnpublishedInternshipPageSeeMoreText({
    i18n,
    orgUrl: unpublishedInternship.org
      ? `${unpublishedInternship.org.url[locale]}#opportunities`
      : undefined,
    orgPublishedInternshipsCount:
      unpublishedInternship.orgPublishedInternshipsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Internship')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Internship Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        recommendedListings.length > 0 && (
          <SearchHitCarousel
            headline={i18n('Explore Similar Internships on Idealist')}
            results={recommendedListings}
            seeAll={{
              text: i18n('See all internships'),
              action: { type: 'button', onClick: () => search('') },
            }}
            listingType="INTERNSHIP"
            carouselLocation="Internship Listing"
            fromListingId={unpublishedInternship.id}
          />
        )
      }
    />
  );
}
